import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleNotch, WarningCircle } from '@phosphor-icons/react';
import { useAuth } from '@/lib/hooks/useAuth';
import { errorMessage } from '@/lib/api';

export function OAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeOAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const code = params.get('code');
    const returnTo = params.get('returnTo');
    const providerError = params.get('error');

    if (providerError) {
      setError(providerError);
      return;
    }
    if (!code) {
      setError('Missing authorization code');
      return;
    }

    completeOAuth(code)
      .then(() => {
        navigate(returnTo && returnTo.startsWith('/') ? returnTo : '/app', { replace: true });
      })
      .catch((err) => {
        setError(errorMessage(err, 'Could not complete sign-in.'));
      });
  }, [params, completeOAuth, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas text-ink">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-board p-8 text-center">
        {error ? (
          <>
            <span className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-700">
              <WarningCircle size={18} weight="fill" />
            </span>
            <h1 className="mb-2 font-display text-lg font-semibold">Sign-in failed</h1>
            <p className="mb-6 text-sm text-ink-muted">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/sign-in', { replace: true })}
              className="rounded-md bg-ink px-4 py-2 text-xs font-medium text-board hover:bg-ink/90"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <span className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-line bg-canvas text-ink">
              <CircleNotch size={18} className="animate-spin" />
            </span>
            <h1 className="mb-2 font-display text-lg font-semibold">Finishing sign-in…</h1>
            <p className="text-sm text-ink-muted">One moment while we set things up.</p>
          </>
        )}
      </div>
    </div>
  );
}
